import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { currentPostAtom } from "../../../atom";

import { ACCESS_TOKEN, getCookie } from "../../../lib/cookie";

import {
  Button,
  Modal,
  Flex,
  Form,
  Input,
  Select,
  Switch,
  Upload,
  notification,
  message,
} from "antd";
import UploadProgressModal from "../../upload/UploadProgressModal";
import { isUploadCancelled, useTusCancel } from "../../../hooks/useTusCancel";
import { resumeOnlySameTarget, createSlideUpload } from "../../../lib/tusUpload";
import { UploadOutlined } from "@ant-design/icons";

import { getPostData } from "../../../api/postAPI";
import { PostCase } from "../../../api/postCaseAPI";
import { MediaGroup, createMediaGroup, setMediaGroupItems } from "../../../api/mediaGroupAPI";
import { groupIdOf } from "../mediaGroup";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import { useSlideQC } from "../../../hooks/useSlideQC";
import { SlideQCModal } from "../../upload/SlideQCModal";
import CaseFileGroups, {
  CaseFileGroup,
  UNASSIGNED_VALUE,
  UPLOAD_MODAL_BODY_STYLE,
  createCaseFileGroup,
  findDuplicateAcrossGroups,
  flattenCaseFileGroups,
} from "./CaseFileGroups";

interface Props {
  showUploadModal: boolean;
  setShowUploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  targetPostId?: number; // 명시적으로 지정할 포스트 ID (없으면 currentPost 사용)
  // [통합] board 모드에서는 이미지도 슬라이드 API로 업로드하므로 all 허용
  fileMode?: "slide" | "all";
  cases?: PostCase[];
  defaultCaseId?: number | null;
  /** 슬라이드 미디어 그룹 목록 (없으면 그룹 지정 UI 미노출) */
  groups?: MediaGroup[];
  /** 업로드한 슬라이드를 그룹에 배정한 뒤 호출 (상위에서 그룹·미디어 목록 새로고침) */
  onGroupAssigned?: () => void;
}

const NEW_GROUP_VALUE = "__new__";

const AddSlideModal = ({
  showUploadModal,
  setShowUploadModal,
  targetPostId,
  fileMode = "slide",
  cases = [],
  defaultCaseId = null,
  groups = [],
  onGroupAssigned,
}: Props) => {
  const [showProgressModal, setShowProgressModal] = useState<boolean>(false);
  const [currentProgress, setCurrentProgress] = useState<number>(0);
  const [currentPost, setCurrentPost] = useRecoilState(currentPostAtom);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [totalProgress, setTotalProgress] = useState<number>(0);
  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [uploadingFileName, setUploadingFileName] = useState<string>("");
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
  const [docModalVisible, setDocModalVisible] = useState<boolean>(false);
  const [useGroupId, setUseGroupId] = useState<boolean>(false);
  // 업로드 후 배정할 그룹. number = 기존 그룹, NEW_GROUP_VALUE = 새로 만들기
  const [groupTarget, setGroupTarget] = useState<number | string | null>(null);
  const [newGroupName, setNewGroupName] = useState<string>("");
  const { showQCModal, qcItems, qcDone, qcHasIssue, runQC, closeQCModal } = useSlideQC();
  const tusCancel = useTusCancel();
  const [slideNames, setSlideNames] = useState<Record<string, string>>({});
  const [caseGroups, setCaseGroups] = useState<CaseFileGroup[]>(() => [createCaseFileGroup()]);

  const [form] = Form.useForm<any>();

  const manualDoc = [{ uri: "/pdf/mrxs_upload_manual_ver1.3.pdf" }];

  useEffect(() => {
    if (showUploadModal) {
      setCaseGroups([createCaseFileGroup(defaultCaseId ?? UNASSIGNED_VALUE)]);
    }
  }, [showUploadModal, defaultCaseId]);

  const handlePreviewCancel = () => {
    setDocModalVisible(false);
  };

  const handleCancel = () => {
    form.resetFields();
    setUseGroupId(false);
    setGroupTarget(null);
    setNewGroupName("");
    setSlideNames({});
    setCaseGroups([createCaseFileGroup()]);
    setShowUploadModal(false);
  };

  /**
   * 업로드가 끝난 뒤 새로 생긴 슬라이드를 그룹에 배정한다.
   * (구 방식의 TUS 메타데이터 groupId 를 대체 — 그룹은 /api/media-groups 가 관리)
   * 새 슬라이드는 항상 그룹없음 상태이므로 원 그룹에서 빼는 과정은 필요 없다.
   */
  const assignUploadedToGroup = async (uploadPostId: number, prevSlideIds: Set<number>) => {
    if (!useGroupId || groupTarget == null) return;
    if (groupTarget === NEW_GROUP_VALUE && !newGroupName.trim()) return;

    try {
      const post = await getPostData(uploadPostId);
      const allSlides: any[] = post?.slides ?? [];
      const newIds = allSlides
        .map((s) => s.slideId)
        .filter((id: number) => id != null && !prevSlideIds.has(id));
      if (newIds.length === 0) return;

      if (groupTarget === NEW_GROUP_VALUE) {
        await createMediaGroup({
          postId: uploadPostId,
          mediaType: "SLIDE",
          name: newGroupName.trim(),
          mediaIds: newIds,
        });
      } else {
        // items 는 교체 방식이라 기존 멤버를 함께 보내야 한다
        const groupId = Number(groupTarget);
        const existing = allSlides
          .filter((s) => groupIdOf(s) === groupId)
          .map((s) => s.slideId);
        await setMediaGroupItems(groupId, Array.from(new Set([...existing, ...newIds])));
      }
      onGroupAssigned?.();
    } catch (e: any) {
      console.error("uploaded slide group assign error:", e);
      message.error(e?.message || "업로드는 완료됐지만 그룹 배정에 실패했습니다.");
    }
  };

  const uploadFile = async () => {
    // 1. 유효성 검사
    if (!targetPostId && !currentPost?.id) return;
    if (isSubmitting) return;

    const entries = flattenCaseFileGroups(caseGroups);
    if (entries.length === 0) {
      message.error("Please upload at least one file");
      return;
    }
    const duplicated = findDuplicateAcrossGroups(caseGroups);
    if (duplicated) {
      message.error(`${duplicated}: 여러 Case 에 같은 파일이 들어 있습니다.`);
      return;
    }
    setIsSubmitting(true);

    // 2. 초기 설정
    const files = entries.map(({ item }) => item.originFileObj);
    const uploadPostId = targetPostId || currentPost.id;
    let prevSlideIds: Set<number>;
    if (uploadPostId === currentPost?.id) {
      prevSlideIds = new Set((currentPost.slides || []).map((s: any) => s.slideId));
    } else {
      const basePost = await getPostData(uploadPostId);
      prevSlideIds = new Set((basePost?.slides || []).map((s: any) => s.slideId));
    }

    setTotalFiles(files.length);
    setShowProgressModal(true);
    tusCancel.begin();
    // 바이트 전송까지 끝난 파일 수 (중지 시 안내용)
    let sentCount = 0;

    const tracker = { successCount: 0 };
    const totalCount = files.length;

    const HEAVY_PROCESS_EXTENSIONS = ['zip', 'mrxs', 'ndpi'];

    const hasHeavyFile = files.some((file: any) => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      return extension && HEAVY_PROCESS_EXTENSIONS.includes(extension);
    });

    try {
      // 3. 파일 순회 업로드
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const { item: fileItem, postCaseId } = entries[i];
        const slideName = slideNames[fileItem.uid] || '';
        tusCancel.throwIfCancelled();
        setUploadingFileName(file.name);
        setCurrentFileIndex(i + 1);

        await new Promise<void>((resolve, reject) => {
          let isResolved = false;

          const upload = createSlideUpload(file, {
            endpoint: process.env.REACT_APP_BACKEND_URL + "/api/slide/tus",
            headers: { Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN)}` },
            chunkSize: 1024 * 1024 * 5,
            // 성공한 업로드의 이어올리기 정보는 남길 필요가 없다
            removeFingerprintOnSuccess: true,
            metadata: {
              postId: String(uploadPostId),
              filename: file.name || "",
              slidename: slideName,
              ...(postCaseId ? { postCaseId: String(postCaseId) } : {}),
            },
            onError: (error) => {
              reject(error);
            },
            onProgress: function (bytesUploaded, bytesTotal) {
              const currentFilePercentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
              setCurrentProgress(Number(currentFilePercentage));

              const completedFiles = i;
              const overallPercentage = ((completedFiles + (bytesUploaded / bytesTotal)) / files.length * 100).toFixed(2);
              setTotalProgress(Number(overallPercentage));

              if (bytesUploaded === bytesTotal) {
                if (!isResolved) {
                  isResolved = true;
                  tusCancel.markSent(upload);
                  setTimeout(() => {
                    resolve();
                  }, 500);
                }
              }
            },
            onSuccess: async () => {
              tracker.successCount++;
              if (tracker.successCount === totalCount) {

                message.success(`${files.length}개의 파일 처리가 완료되었습니다.`);

                if (currentPost.id === uploadPostId) {
                  const updatedPost = await getPostData(uploadPostId);
                  setCurrentPost(updatedPost);
                } else {
                    // 사용자가 이미 자리를 떴으므로 굳이 화면을 갱신하지 않음
                }
              }
            },
          }, { isCancelled: tusCancel.isCancelled });

          tusCancel.track(upload, reject);

          // 같은 게시물·Case·이름으로 올리던 업로드만 이어올린다 (다른 대상이면 새로 업로드)
          resumeOnlySameTarget(upload)
            .catch((e) => {
              // 확인에 실패하면 이어올리지 않고 새로 올린다
              console.warn("tus resume check failed:", e);
            })
            .then(() => {
              // 이어올리기 정보를 확인하는 사이 중지됐으면 시작하지 않는다
              if (tusCancel.isCancelled()) return;
              upload.start();
            });
        });
        sentCount++;
      }

      // 4. 업로드된 슬라이드를 선택한 그룹에 배정 (TUS 메타데이터 groupId 대체)
      await assignUploadedToGroup(uploadPostId, prevSlideIds);

      // 5. [UX] 루프 종료 후 UI 닫기
      setShowProgressModal(false);
      setShowUploadModal(false);
      form.resetFields();
      setUseGroupId(false);
      setGroupTarget(null);
      setNewGroupName("");
      setIsSubmitting(false);
      setSlideNames({});
      setCaseGroups([createCaseFileGroup()]);

      runQC(uploadPostId, prevSlideIds, files.map((f: File) => f.name), async () => {
        if (currentPost?.id === uploadPostId) {
          const updated = await getPostData(uploadPostId);
          setCurrentPost(updated);
        }
      });

      if (hasHeavyFile) {
        notification.info({
          message: '대용량 파일 전송 완료',
          description: '파일 전송은 완료되었으나, 서버 처리에 시간이 소요될 수 있습니다. 처리가 끝나면 목록이 자동으로 갱신됩니다.',
          duration: 10,
          placement: 'bottomRight',
        });
      } else {
        // (선택 사항) 가벼운 파일은 그냥 짧은 성공 메시지만 띄우거나 아예 생략
        // message.success("업로드가 완료되었습니다."); 
      }

    } catch (error) {
      setShowProgressModal(false);
      setIsSubmitting(false);
      setSlideNames({});
      setCaseGroups([createCaseFileGroup()]);

      if (isUploadCancelled(error)) {
        // 중지: 이미 전송된 파일만 등록된다. 그룹 배정·QC 등 후속 작업은 하지 않는다
        setShowUploadModal(false);
        form.resetFields();
        setUseGroupId(false);
        setGroupTarget(null);
        setNewGroupName("");
        notification.warning({
          message: "업로드를 중지했습니다",
          description:
            sentCount > 0
              ? `전체 ${files.length}개 중 전송이 끝난 ${sentCount}개 파일은 등록됩니다. 필요 없으면 목록에서 삭제해주세요.`
              : "등록된 파일은 없습니다.",
          duration: 8,
          placement: "bottomRight",
        });
        if (currentPost?.id === uploadPostId) {
          setCurrentPost(await getPostData(uploadPostId));
        }
        return;
      }
      console.error("Upload error:", error);
      if (sentCount >= files.length) {
        // 파일 전송은 모두 끝났고 그룹 배정 등 후속 처리에서 실패
        message.error("파일 전송은 완료됐지만 후속 처리 중 오류가 발생했습니다.");
        return;
      }
      // 재시도·재연결까지 실패: 진행 창만 닫히고 끝나지 않도록 어디서 멈췄는지 안내한다
      setShowUploadModal(false);
      form.resetFields();
      setUseGroupId(false);
      setGroupTarget(null);
      setNewGroupName("");
      notification.error({
        message: "업로드가 중단되었습니다",
        description:
          `전체 ${files.length}개 중 ${sentCount + 1}번째 파일 '${files[sentCount]?.name ?? ""}'에서 중단되었습니다. ` +
          (sentCount > 0 ? `앞의 ${sentCount}개 파일은 등록됩니다. ` : "") +
          "남은 파일을 같은 게시글·같은 슬라이드 이름으로 다시 올리면 중단된 파일은 받은 지점부터 이어서 올라갑니다.",
        duration: 0,
        placement: "bottomRight",
      });
      if (currentPost?.id === uploadPostId) {
        setCurrentPost(await getPostData(uploadPostId));
      }
    }
  };

  return (
    <>
      <Modal
        centered
        title="Upload Slide"
        styles={{ body: UPLOAD_MODAL_BODY_STYLE }}
        open={showUploadModal}
        onCancel={handleCancel}
        footer={null}
      >
        <Flex gap="middle" vertical>
          <Form form={form} onFinish={uploadFile} layout="vertical">
            <Button type="link" onClick={() => setDocModalVisible(true)} style={{ marginBottom: "8px" }}>
              Show Upload Manual
            </Button>

            {/* 그룹은 업로드 완료 후 /api/media-groups 로 배정한다 (TUS 메타데이터 전송 X) */}
            <Form.Item label="그룹 지정" style={{ marginBottom: useGroupId ? '8px' : '16px' }}>
              <Switch
                checked={useGroupId}
                onChange={(checked) => {
                  setUseGroupId(checked);
                  if (checked) {
                    setGroupTarget(groups.length > 0 ? groups[0].mediaGroupId : NEW_GROUP_VALUE);
                  } else {
                    setGroupTarget(null);
                    setNewGroupName("");
                  }
                }}
              />
            </Form.Item>
            {useGroupId && (
              <Form.Item label="그룹">
                <Select
                  value={groupTarget ?? undefined}
                  onChange={(v) => setGroupTarget(v)}
                  options={[
                    ...groups.map((g) => ({ label: g.name, value: g.mediaGroupId })),
                    { label: "＋ 새 그룹 만들기", value: NEW_GROUP_VALUE },
                  ]}
                />
                {groupTarget === NEW_GROUP_VALUE && (
                  <Input
                    style={{ marginTop: 8 }}
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="새 그룹 이름"
                    maxLength={255}
                  />
                )}
              </Form.Item>
            )}
            <CaseFileGroups
              cases={cases}
              groups={caseGroups}
              setGroups={setCaseGroups}
              mode={fileMode}
              renderFiles={(group) =>
                group.fileList.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ marginBottom: '8px', fontWeight: 500 }}>슬라이드 이름 입력</div>
                    {group.fileList.map((item: any) => (
                      <div key={item.uid} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                        <span style={{ flex: '0 0 auto', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#595959', fontSize: '12px' }}>
                          {item.name}
                        </span>
                        <Input
                          placeholder="슬라이드 이름 (선택)"
                          value={slideNames[item.uid] || ''}
                          onChange={(e) => setSlideNames(prev => ({ ...prev, [item.uid]: e.target.value }))}
                          style={{ flex: 1 }}
                        />
                      </div>
                    ))}
                  </div>
                )
              }
            />
            <Form.Item style={{ marginTop: "20px" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={handleCancel} style={{ marginRight: "10px" }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Flex>
      </Modal>

      <UploadProgressModal
        open={showProgressModal}
        fileName={uploadingFileName}
        fileIndex={currentFileIndex}
        totalFiles={totalFiles}
        currentPercent={currentProgress}
        totalPercent={totalProgress}
        onCancel={tusCancel.cancel}
        cancelling={tusCancel.cancelling}
      />

      {/* 전체화면 모달 */}
      <Modal
        centered
        title="Upload Manual"
        open={docModalVisible}
        onCancel={handlePreviewCancel}
        width={"100%"}
        footer={null}
      >
        <DocViewer
          documents={manualDoc}
          pluginRenderers={DocViewerRenderers}
          config={{
            header: { disableHeader: true },
            pdfZoom: {
              defaultZoom: 0.5,
              zoomJump: 0.1,
            },
            pdfVerticalScrollByDefault: true,
          }}
        />
      </Modal>

      <SlideQCModal
        open={showQCModal}
        items={qcItems}
        done={qcDone}
        hasIssue={qcHasIssue}
        onClose={closeQCModal}
      />
    </>
  );
};

export default AddSlideModal;